import { useTranslation } from 'react-i18next';
import { RequestStatus } from '@magimp/shared';
import { Button } from '../../../ui/index.js';
import { useDraftDeleteArm } from '../useDraftDeleteArm.js';
import { ErrorChip, Status } from './DraftEditorPrimitives.js';
import type { DraftScratchpad } from '../types.js';

export interface DraftEditorFooterProps {
  d: DraftScratchpad;
  dirty: boolean;
  saving: boolean;
  sending: boolean;
  closing: boolean;
  deleting: boolean;
  uploading: boolean;
  localUploading: boolean;
  anyUploading: boolean;
  persistedId: string | null;
  requiredErrors: string[];
  saveError: string | null;
  sendError: string | null;
  closeError: string | null;
  deleteError: string | null;
  onSave: () => void;
  onSend: () => void;
  onDelete: () => Promise<void> | void;
}

export function DraftEditorFooter(props: DraftEditorFooterProps) {
  const { t } = useTranslation(['requests']);
  const {
    d,
    dirty,
    saving,
    sending,
    closing,
    deleting,
    uploading,
    localUploading,
    anyUploading,
    persistedId,
    requiredErrors,
    saveError,
    sendError,
    closeError,
    deleteError,
    onSave,
    onSend,
    onDelete,
  } = props;

  // Disable destructive delete while ANY of upload/save/send/close is
  // racing — silently swallowing a click would let the user think they
  // armed delete when they did not. `uploading` is the store-level
  // counter view; the local `itemUploading` map covers the brief
  // window before `beginPhotoUpload` lands. The matching i18n hint
  // surfaces the upload-in-flight reason explicitly.
  const deleteDisabled = closing || sending || saving || deleting || uploading || localUploading;

  const { deleteArmed, handleDeleteClick } = useDraftDeleteArm({
    deleteDisabled,
    deleteError,
    onDelete,
  });

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        padding: '10px 0',
        flexWrap: 'wrap',
      }}
    >
      <DraftStatusLine
        dirty={dirty}
        saving={saving}
        persisted={persistedId !== null}
        requiredErrors={requiredErrors}
      />
      {saveError && <ErrorChip text={saveError} />}
      {sendError && <ErrorChip text={sendError} />}
      {closeError && <ErrorChip text={closeError} />}
      {deleteError && <ErrorChip text={deleteError} />}
      <div style={{ flex: 1 }} />
      <Button
        kind={deleteArmed ? 'danger' : 'ghost'}
        icon={deleteArmed ? 'alert-triangle' : 'trash-2'}
        disabled={deleteDisabled}
        aria-describedby="draft-editor-delete-hint"
        onClick={handleDeleteClick}
      >
        {deleteArmed
          ? t('requests:draft.deleteConfirm.confirm')
          : d.status === RequestStatus.RETURN
            ? t('requests:draft.deleteReturn')
            : t('requests:draft.deleteDraft')}
      </Button>
      {/* Polite live region announces the armed/uploading hint to
          assistive tech without stealing focus. The wrapping div
          stays in the DOM so screen readers track content swaps. */}
      <div
        id="draft-editor-delete-hint"
        aria-live="polite"
        aria-atomic="true"
        style={{
          position: 'absolute',
          width: 1,
          height: 1,
          padding: 0,
          margin: -1,
          overflow: 'hidden',
          clip: 'rect(0 0 0 0)',
          whiteSpace: 'nowrap',
          border: 0,
        }}
      >
        {deleteDisabled && (uploading || localUploading)
          ? t('requests:draft.deleteConfirm.uploadingHint')
          : deleteArmed
            ? t('requests:draft.deleteConfirm.hint')
            : ''}
      </div>
      <Button
        kind="secondary"
        icon="save"
        disabled={
          !dirty ||
          saving ||
          sending ||
          closing ||
          anyUploading ||
          // WHY (#700 / #1057): STANDARD draft-save rejects an empty
          //   destinations list with 400 — gate here so the user gets a
          //   disabled-Save signal instead of an opaque server error.
          //   CONTAINER draft-save REQUIRES `destinations: []` (the
          //   wire rejects a non-empty list), so the STANDARD gate must
          //   NOT apply on the CONTAINER branch — and CONTAINER's own
          //   `deliveryAddress` is relaxed on draft-save (a null value
          //   persists until typing finishes; send-readiness reasserts
          //   non-empty), so no CONTAINER-side draft-save gate is
          //   needed here.
          (d.kind !== 'CONTAINER' && d.destinations.length === 0)
        }
        onClick={onSave}
      >
        {saving
          ? t('requests:draft.saving')
          : d.status === RequestStatus.RETURN
            ? t('requests:draft.saveReturn')
            : t('requests:draft.save')}
      </Button>
      <Button
        kind="primary"
        icon="send-horizontal"
        disabled={requiredErrors.length > 0 || anyUploading || sending || saving || closing}
        onClick={onSend}
      >
        {t('requests:actions.send')}
      </Button>
    </div>
  );
}

function DraftStatusLine({
  dirty,
  saving,
  persisted,
  requiredErrors,
}: {
  dirty: boolean;
  saving: boolean;
  persisted: boolean;
  requiredErrors: string[];
}) {
  const { t } = useTranslation(['requests']);
  if (saving) {
    return (
      <Status icon="loader" tone="muted">
        {t('requests:draft.saving')}
      </Status>
    );
  }
  if (requiredErrors.length) {
    return (
      <Status icon="alert-circle" tone="warn">
        {t('requests:draft.required.prefix')}{' '}
        <b style={{ color: 'var(--fg-2)', fontWeight: 500 }}>{requiredErrors.join(', ')}</b>
      </Status>
    );
  }
  if (dirty) {
    return (
      <Status icon="circle-dot" tone="muted">
        {persisted ? t('requests:draft.unsavedChanges') : t('requests:draft.unsavedDraft')}
      </Status>
    );
  }
  if (persisted) {
    return (
      <Status icon="check-circle-2" tone="success">
        {t('requests:draft.savedReady')}
      </Status>
    );
  }
  return (
    <Status icon="check-circle-2" tone="success">
      {t('requests:draft.ready')}
    </Status>
  );
}
